import React, { useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { formatPrice } from 'legacyfrontend/helpers/formatPrice';

const PriceBreakdown = ({ flightData, selectedBaggage = {}, onTotalPriceChange }) => {
  if (!flightData) return <></>;

  const { passengers, price } = flightData;

  const priceCurrency = useMemo(() => {
    return price.currency;
  }, [price]);

  const passengerLabels = {
    adults: 'Adulto',
    children: 'Niño',
    baby: 'Bebé',
  };

  const passengerTypes = Object.entries(passengers)
    .map(([type, data]) => ({
      type,
      label: passengerLabels[type],
      quantity: data.quantity || 0,
      fares: data.base || 0,
      taxes: data.total_taxes || 0,
      fee: data.fee || 0,
      emission_fee: data.emission_fee || 0,
      total: data.total || 0,
    }))

    .filter((passenger) => passenger.quantity > 0);

  const processIndividualBaggage = () => {
    const passengersMap = new Map();

    Object.entries(selectedBaggage).forEach(([passengerId, services]) => {
      const [type, number] = passengerId.split('_');
      const label = type === 'adult' ? 'Adulto' : type === 'child' ? 'Niño' : 'Bebé';

      const passengerNumber = parseInt(number) + 1;

      const totals = {
        base: services.reduce((sum, service) => sum + service.base, 0),
        fee: services.reduce((sum, service) => sum + service.fee, 0),
        total: services.reduce((sum, service) => sum + service.total, 0),
      };

      passengersMap.set(passengerId, {
        id: passengerId,
        type,
        label: `${label} ${passengerNumber}`,
        orderNumber: passengerNumber,
        ...totals,
        hasServices: services.length > 0,
      });
    });

    passengerTypes.forEach((passenger) => {
      const baseType =
        passenger.type === 'adults' ? 'adult' : passenger.type === 'children' ? 'child' : 'baby';

      for (let i = 0; i < passenger.quantity; i++) {
        const passengerId = `${baseType}_${i}`;
        if (!passengersMap.has(passengerId)) {
          passengersMap.set(passengerId, {
            id: passengerId,
            type: baseType,
            label: `${passenger.label} ${i + 1}`,
            orderNumber: i + 1,
            base: 0,
            fee: 0,
            total: 0,
            hasServices: false,
          });
        }
      }
    });

    return Array.from(passengersMap.values()).sort((a, b) => {
      const typeOrder = {
        adult: 1,
        child: 2,
        baby: 3,
      };

      if (typeOrder[a.type] !== typeOrder[b.type]) {
        return typeOrder[a.type] - typeOrder[b.type];
      }

      return a.orderNumber - b.orderNumber;
    });
  };

  const individualBaggage = processIndividualBaggage();
  const calculateTotalPrice = () => {
    const baggageTotal = individualBaggage.reduce((sum, passenger) => sum + passenger.total, 0);
    return price.total + baggageTotal;
  };

  useEffect(() => {
    const newTotal = calculateTotalPrice();
    onTotalPriceChange?.(newTotal);
  }, [price.total, individualBaggage, onTotalPriceChange]);

  return (
    <>
      <TableContainer component={Paper} elevation={0}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Concepto</TableCell>
              <TableCell align='right'>Tarifas</TableCell>
              <TableCell align='right'>Tasas, Impuestos y Cargos</TableCell>
              <TableCell align='right'>Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Passengers Section */}
            <TableRow>
              <TableCell
                colSpan={5}
                sx={{
                  bgcolor: 'grey.100',
                  fontWeight: 'bold',
                  py: 1,
                }}
              >
                Pasajeros
              </TableCell>
            </TableRow>

            {passengerTypes.map((passenger) => (
              <TableRow key={passenger.type}>
                <TableCell>{`${passenger.quantity}x ${passenger.label}`}</TableCell>
                <TableCell align='right'>
                  {formatPrice(passenger.fares, priceCurrency, true)}
                </TableCell>
                <TableCell align='right'>
                  {formatPrice(
                    passenger.taxes + passenger.fee + passenger.emission_fee,
                    priceCurrency,
                    true
                  )}
                </TableCell>
                <TableCell align='right'>
                  {formatPrice(passenger.total * passenger.quantity, priceCurrency, true)}
                </TableCell>
              </TableRow>
            ))}

            {/* Individual Baggage Section */}
            <TableRow>
              <TableCell
                colSpan={5}
                sx={{
                  bgcolor: 'grey.100',
                  fontWeight: 'bold',
                  py: 1,
                }}
              >
                Equipaje Extra
              </TableCell>
            </TableRow>

            {individualBaggage.map((passenger) => (
              <TableRow key={passenger.id}>
                <TableCell
                  sx={{
                    color: !passenger.hasServices ? 'text.secondary' : 'inherit',
                  }}
                >
                  {passenger.label}
                </TableCell>
                <TableCell align='right'>
                  {passenger.hasServices ? formatPrice(passenger.base, priceCurrency, true) : '-'}
                </TableCell>
                <TableCell align='right'>
                  {passenger.hasServices ? formatPrice(passenger.fee, priceCurrency, true) : '-'}
                </TableCell>
                <TableCell align='right'>
                  {formatPrice(passenger.total, priceCurrency, true)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          mt: 2,
          padding: 1,
          alignItems: 'center',
        }}
      >
        <Typography sx={{ mr: 2 }} variant='h6'>
          Precio Total
        </Typography>
        <Typography variant='h5'>
          {formatPrice(calculateTotalPrice(), priceCurrency, true)}
        </Typography>
      </Box>
    </>
  );
};

export default PriceBreakdown;
